import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Camera, PackageSearch, CalendarHeart, UserPlus, BookMarked, FileText, Download, Loader2, CheckCircle2 } from "lucide-react";
import api, { apiErr, fmtDate, downloadDoc } from "@/lib/api";
import { Loading, Empty, Field } from "@/components/site/ui";
import StatusBadge from "@/components/StatusBadge";
import PhotoUploader from "@/components/portal/PhotoUploader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const inputCls = "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none";

function Stars({ value, onChange, label }) {
  return (
    <div>
      <span className="text-xs tracking-label uppercase text-muted-foreground">{label}</span>
      <div className="flex gap-1 mt-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} aria-label={`${label}: ${n} of 5`} data-testid={`star-${label}-${n}`}>
            <Star size={22} className={n <= value ? "fill-gold text-gold" : "text-white/25"} />
          </button>
        ))}
      </div>
    </div>
  );
}

function FeedbackForm({ reservationId, onDone }) {
  const [f, setF] = useState({ overall: 0, arrival: 0, cleanliness: 0, pool: 0, comfort: 0, concierge: 0,
    favorite: "", improve: "", private_note: "", allow_follow_up: false, allow_public: false, anonymous: false });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.overall) { toast.error("Please share an overall rating."); return; }
    setSaving(true);
    try { await api.post("/portal/feedback", { ...f, reservation_id: reservationId }); toast.success("Thank you — your feedback is private to The D."); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Your feedback is private by default. It is only ever shared publicly with your explicit permission below.</p>
      <div className="grid grid-cols-2 gap-4">
        <Stars label="Overall" value={f.overall} onChange={(v) => set("overall", v)} />
        <Stars label="Arrival" value={f.arrival} onChange={(v) => set("arrival", v)} />
        <Stars label="Cleanliness" value={f.cleanliness} onChange={(v) => set("cleanliness", v)} />
        <Stars label="Pool" value={f.pool} onChange={(v) => set("pool", v)} />
        <Stars label="Comfort" value={f.comfort} onChange={(v) => set("comfort", v)} />
        <Stars label="Concierge" value={f.concierge} onChange={(v) => set("concierge", v)} />
      </div>
      <Field label="What did you love?"><textarea rows={2} className={inputCls} value={f.favorite} onChange={(e) => set("favorite", e.target.value)} data-testid="feedback-favorite" /></Field>
      <Field label="What could we do better?"><textarea rows={2} className={inputCls} value={f.improve} onChange={(e) => set("improve", e.target.value)} /></Field>
      <Field label="A private note to your host"><textarea rows={2} className={inputCls} value={f.private_note} onChange={(e) => set("private_note", e.target.value)} /></Field>
      <div className="space-y-2">
        {[["allow_follow_up", "The D may follow up with me about this feedback"], ["allow_public", "The D may share my comments publicly (a testimonial)"], ["anonymous", "Keep my feedback anonymous"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-3 text-sm cursor-pointer"><input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="accent-gold" data-testid={`feedback-${k}`} /> {l}</label>
        ))}
      </div>
      <button onClick={submit} disabled={saving} className="btn-gold px-6 py-2.5 text-sm flex items-center gap-2" data-testid="feedback-submit">{saving && <Loader2 size={15} className="animate-spin" />} Submit Feedback</button>
    </div>
  );
}

function LostItemForm({ reservationId, onDone }) {
  const [f, setF] = useState({ item: "", location: "", last_seen: "", contact_pref: "email", shipping_address: "", notes: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.item) { toast.error("Please describe the item."); return; }
    try { await api.post("/portal/lost-item", { ...f, reservation_id: reservationId }); toast.success("Reported. The D will search and update you."); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-3">
      <Field label="What did you lose?" required><input className={inputCls} value={f.item} onChange={(e) => set("item", e.target.value)} data-testid="lost-item" /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Where were you last?"><input className={inputCls} value={f.location} onChange={(e) => set("location", e.target.value)} /></Field>
        <Field label="When did you last see it?"><input className={inputCls} value={f.last_seen} onChange={(e) => set("last_seen", e.target.value)} /></Field>
      </div>
      <Field label="How should we reach you?"><select className={inputCls} value={f.contact_pref} onChange={(e) => set("contact_pref", e.target.value)}><option value="email">Email</option><option value="phone">Phone</option></select></Field>
      <Field label="Shipping address (if you'd like it mailed)"><textarea rows={2} className={inputCls} value={f.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} /></Field>
      <Field label="Notes"><textarea rows={2} className={inputCls} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
      <button onClick={submit} className="btn-gold px-6 py-2.5 text-sm" data-testid="lost-submit">Report Lost Item</button>
    </div>
  );
}

function FutureStayForm({ onDone }) {
  const [f, setF] = useState({ preferred_dates: "", experience: "", celebration: "", guests: "", notes: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    try { await api.post("/portal/future-stay", f); toast.success("Noted — your host will reach out to plan."); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-3">
      <Field label="Preferred dates"><input className={inputCls} value={f.preferred_dates} onChange={(e) => set("preferred_dates", e.target.value)} data-testid="future-dates" /></Field>
      <Field label="Experience of interest"><input className={inputCls} value={f.experience} onChange={(e) => set("experience", e.target.value)} /></Field>
      <Field label="Celebration?"><input className={inputCls} value={f.celebration} onChange={(e) => set("celebration", e.target.value)} placeholder="Birthday, anniversary…" /></Field>
      <Field label="Party size"><input className={inputCls} value={f.guests} onChange={(e) => set("guests", e.target.value)} /></Field>
      <Field label="Anything else?"><textarea rows={2} className={inputCls} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
      <button onClick={submit} className="btn-gold px-6 py-2.5 text-sm" data-testid="future-submit">Register Interest</button>
    </div>
  );
}

function ReferralForm({ onDone }) {
  const [f, setF] = useState({ referred_name: "", referred_email: "", message: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.referred_name || !f.referred_email) { toast.error("Name and email are required."); return; }
    try { await api.post("/portal/referral", f); toast.success("Referral sent for host approval."); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Referrals require host approval before an invitation is issued.</p>
      <Field label="Their name" required><input className={inputCls} value={f.referred_name} onChange={(e) => set("referred_name", e.target.value)} data-testid="referral-name" /></Field>
      <Field label="Their email" required><input className={inputCls} value={f.referred_email} onChange={(e) => set("referred_email", e.target.value)} data-testid="referral-email" /></Field>
      <Field label="A personal note"><textarea rows={3} className={inputCls} value={f.message} onChange={(e) => set("message", e.target.value)} /></Field>
      <button onClick={submit} className="btn-gold px-6 py-2.5 text-sm" data-testid="referral-submit">Send Referral</button>
    </div>
  );
}

function GuestbookForm({ onDone }) {
  const [f, setF] = useState({ name: "", message: "", allow_public: true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Please add your name."); return; }
    if (!f.message.trim()) { toast.error("Please write a message."); return; }
    try { await api.post("/public/guestbook", f); toast.success("Thank you for signing the guestbook."); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-3">
      <Field label="Signed as" required><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" data-testid="guestbook-name" /></Field>
      <Field label="Your message" required><textarea rows={3} className={inputCls} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="guestbook-message" /></Field>
      <label className="flex items-center gap-3 text-sm cursor-pointer"><input type="checkbox" checked={f.allow_public} onChange={(e) => set("allow_public", e.target.checked)} className="accent-gold" /> May be shown publicly (after host approval)</label>
      <button onClick={submit} className="btn-gold px-6 py-2.5 text-sm" data-testid="guestbook-submit">Sign the Guestbook</button>
    </div>
  );
}

function ActionCard({ icon: Icon, title, desc, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={testid} className="glass rounded-lg p-5 text-left hover:glow-gold transition-shadow w-full">
          <Icon className="text-gold mb-3" size={22} />
          <p className="font-serif text-lg">{title}</p>
          <p className="text-xs text-muted-foreground mt-1">{desc}</p>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{title}</DialogTitle></DialogHeader>
        {typeof children === "function" ? children(() => setOpen(false)) : children}
      </DialogContent>
    </Dialog>
  );
}

export default function PortalPostStay() {
  const [res, setRes] = useState(null);
  const [albums, setAlbums] = useState([]);
  const load = () => {
    api.get("/portal/reservations").then((r) => setRes(r.data)).catch(() => setRes([]));
    api.get("/portal/albums").then((r) => setAlbums(r.data)).catch(() => setAlbums([]));
  };
  useEffect(() => { load(); }, []);

  if (res === null) return <Loading />;
  const stays = res.filter((r) => ["Checked Out", "Archived"].includes(r.status) || r.post_stay_status);
  const latest = stays[0];

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs tracking-label uppercase text-pool">After your stay</p>
        <h2 className="font-serif text-3xl mt-1">Post-Stay</h2>
        <p className="text-sm text-muted-foreground mt-2">Everything to close the loop on your time at The D — with love, from your host.</p>
      </div>

      {stays.length === 0 ? (
        <Empty title="No completed stays yet" text="Your post-stay dashboard appears here after checkout." />
      ) : (
        <>
          <div className="glass rounded-lg p-6" data-testid="poststay-status">
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <p className="text-xs tracking-label uppercase text-muted-foreground">{latest.confirmation_number}</p>
                <h3 className="font-serif text-2xl mt-1">{latest.experience_title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{fmtDate(latest.arrival_date)} → {fmtDate(latest.departure_date)}</p>
              </div>
              <div className="text-right space-y-2">
                <StatusBadge status={latest.status} />
                {latest.post_stay_status && <p className="text-xs text-pool flex items-center gap-1 justify-end"><CheckCircle2 size={13} /> {latest.post_stay_status}</p>}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ActionCard testid="card-feedback" icon={Star} title="Share Feedback" desc="Private by default — help us grow.">
              {(done) => <FeedbackForm reservationId={latest.id} onDone={() => { done(); load(); }} />}
            </ActionCard>
            <ActionCard testid="card-lost" icon={PackageSearch} title="Report a Lost Item" desc="Left something behind? We'll find it.">
              {(done) => <LostItemForm reservationId={latest.id} onDone={done} />}
            </ActionCard>
            <ActionCard testid="card-future" icon={CalendarHeart} title="Plan a Future Visit" desc="Tell us when you'd love to return.">
              {(done) => <FutureStayForm onDone={done} />}
            </ActionCard>
            <ActionCard testid="card-referral" icon={UserPlus} title="Refer a Friend" desc="Invite someone special (host-approved).">
              {(done) => <ReferralForm onDone={done} />}
            </ActionCard>
            <ActionCard testid="card-guestbook" icon={BookMarked} title="Sign the Guestbook" desc="Leave a note for The D.">
              {(done) => <GuestbookForm onDone={done} />}
            </ActionCard>
            <ActionCard testid="card-docs" icon={FileText} title="Final Documents" desc="Download your receipt & records.">
              <div className="space-y-2">
                {[["Final Receipt", `/portal/documents/reservation/${latest.id}/receipt`, `${latest.confirmation_number}-receipt.pdf`],
                  ["Folio Statement", `/portal/documents/reservation/${latest.id}/folio`, `${latest.confirmation_number}-folio.pdf`],
                  ["Check-In Record", `/portal/documents/reservation/${latest.id}/checkin-record`, `${latest.confirmation_number}-checkin.pdf`]].map(([label, path, fn]) => (
                  <button key={label} onClick={() => downloadDoc(path, fn).catch(() => toast.error("Download failed."))} data-testid={`doc-${label}`}
                    className="btn-ghost-gold w-full py-2 text-sm flex items-center justify-center gap-2"><Download size={14} /> {label}</button>
                ))}
              </div>
            </ActionCard>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4"><Camera className="text-gold" size={20} /><h3 className="font-serif text-2xl">Your Photo Albums</h3></div>
            {albums.length === 0 ? (
              <Empty title="No shared albums yet" text="When your host shares an album, you can view and add your own photos here." />
            ) : (
              <div className="space-y-6">
                {albums.map((a) => (
                  <div key={a.id} className="glass rounded-lg p-6" data-testid={`album-${a.id}`}>
                    <div className="flex justify-between items-center mb-3">
                      <div><p className="font-serif text-xl">{a.title}</p><p className="text-xs text-muted-foreground">{a.description}</p></div>
                      {a.downloads_enabled && <button onClick={() => downloadDoc(`/portal/albums/${a.id}/download`, `album-${a.id.slice(0,8)}.zip`).catch(() => toast.error("Download failed."))} className="btn-ghost-gold px-4 py-2 text-xs flex items-center gap-2"><Download size={13} /> Download all</button>}
                    </div>
                    {(a.photos || []).length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                        {a.photos.map((p) => <img key={p.id} src={`${p.thumbnail_url || p.url || ""}`} loading="lazy" alt={p.caption || "Guest photo"} className="aspect-square object-cover rounded-md" />)}
                      </div>
                    )}
                    {a.uploads_enabled && <PhotoUploader albumId={a.id} onUploaded={load} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
